import React from 'react';
import { Link } from 'react-router-dom';
import { Zap } from 'lucide-react';
import useCredits, { creditCost } from '../hooks/useCredits';

const formatDate = (iso) => {
  if (!iso) return null;
  // Periods end at UTC midnight: show that calendar date, not the (possibly earlier) local one
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
};

/** Header meter: credits left, monthly allowance and renewal date. Links to billing. */
export const CreditMeter = () => {
  const { credits } = useCredits();
  if (!credits) return null;

  const low = !credits.unlimited && credits.allowance > 0 && credits.balance < credits.allowance * 0.2;
  const renews = formatDate(credits.renews_at);
  const title = credits.unlimited
    ? 'Unlimited AI credits (admin)'
    : `${credits.balance} AI credits available${renews ? ` · renews ${renews}` : ''}`
      + (credits.is_team_pool ? ' · shared team credits' : '');

  return (
    <Link
      to="/settings?tab=billing"
      title={title}
      data-testid="credit-meter"
      className={`flex items-center gap-1.5 whitespace-nowrap rounded-lg px-2.5 py-1.5 text-sm font-medium transition-colors ${
        low ? 'bg-amber-50 text-amber-700 hover:bg-amber-100' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
      }`}
    >
      <Zap className="h-4 w-4" />
      {credits.unlimited ? (
        <span>Unlimited</span>
      ) : (
        <span>
          {credits.balance.toLocaleString()}
          {credits.allowance > 0 && credits.balance <= credits.allowance && (
            <span className="hidden sm:inline font-normal opacity-75"> / {credits.allowance.toLocaleString()}</span>
          )}
          <span className="hidden md:inline font-normal opacity-75"> credits</span>
        </span>
      )}
    </Link>
  );
};

/** "· 3 credits" label for an AI button, from the live price list. */
export const CreditCost = ({ feature, seconds, units, className = '' }) => {
  const { prices } = useCredits();
  const cost = creditCost(prices, feature, { seconds, units });
  if (cost == null) return null;
  return (
    <span className={`ml-1 text-xs font-normal opacity-80 ${className}`} data-testid={`credit-cost-${feature}`}>
      · {cost} {cost === 1 ? 'credit' : 'credits'}
    </span>
  );
};

export default CreditMeter;
